import { cn } from "~/lib/utils";
import { ProjectChip, type ProjectChipProps } from "../newThread/composerChips";
import type { HeadingSegments } from "../newThread/NewThreadLanding.logic";

export interface DraftHeroHeadlineProps {
  readonly headingSegments: HeadingSegments;
  /**
   * Omit to render the name as text rather than a control.
   *
   * The chat surface knows its `activeProject` but has no project *list* and
   * no selection callbacks, so it cannot yet hand this over — threading that
   * source through `ChatView` is its own change. Rendering plain text there is
   * exactly what it does today; what this fixes now is that both surfaces stop
   * disagreeing about the copy and the weight. When the data source arrives
   * there is one place to pass it, not a second headline to remember.
   */
  readonly project?: ProjectChipProps;
  readonly className?: string;
}

/**
 * The draft's headline, with the project name as the picker itself.
 *
 * Ported from t3code's `DraftHeroHeadline`, which reads
 * `What should we build in {selector}?` and puts its `MenuTrigger` inside the
 * sentence so the popup opens centred under the name it changes.
 *
 * Shared because Forge rendered this sentence twice and the two copies had
 * already drifted: the `/new` landing built it from `buildHeadingSegments` at
 * `font-normal` with a working picker, while `MessagesTimeline`'s empty state
 * hard-coded the same words at `font-medium` as dead text. Same screen to a
 * user, two components to us, and only one of them could change a project.
 *
 * The reference is their component at this same path, which reads
 * `text-center font-normal text-2xl tracking-tight sm:text-3xl` (plus
 * `mx-auto w-full max-w-5xl`, which is not ported — `MessagesTimeline` passes
 * its own `max-w-2xl`, so width belongs to the caller here).
 *
 * An earlier change set this to `font-semibold` with normal tracking and
 * `leading-tight`, measured off their `EmptyTitle`. That primitive backs the
 * `NoProjectsHero` in their `_chat.index.tsx` — the empty state shown only when
 * the project list is empty, and the only hero a project-less instance can
 * render. Role-equivalence, not a rendered number, is what makes a parity
 * comparison sound; same path and same name is the cheapest proof of it.
 * Growing at `sm` came from that same change and was right, so it stays.
 *
 * Note the mobile heading carries no control — the inline trigger is
 * desktop-only, and touch reaches the picker through `NewThreadMobileSetup`'s
 * dialog instead.
 */
export function DraftHeroHeadline({ headingSegments, project, className }: DraftHeroHeadlineProps) {
  return (
    <h1
      className={cn(
        "text-center font-normal text-2xl tracking-tight text-foreground sm:text-3xl",
        className,
      )}
    >
      <span className="md:hidden">What should we build?</span>
      <span className="hidden md:inline">
        {headingSegments.prefix}
        {project ? <ProjectChip {...project} variant="inline" /> : headingSegments.projectName}
        {headingSegments.suffix}
      </span>
    </h1>
  );
}
